import { FirebaseError } from "firebase/app"
import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  GoogleAuthProvider,
  getAuth,
  type NextOrObserver,
  OAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
  updatePassword,
  verifyBeforeUpdateEmail,
} from "firebase/auth"
import { buildAuthContinueUrl } from "@/lib/auth-continue-url"
import {
  AccountExistsWithDifferentCredentialError,
  EmailAlreadyInUseError,
  InvalidCredentialError,
  UnauthorizedError,
  UnknownError,
} from "@/lib/errors"

import { app } from "./app"

const googleAuthProvider = new GoogleAuthProvider()
const lineAuthProvider = new OAuthProvider("oidc.line").addScope("email")

export const auth = {
  setLanguageCode: (languageCode: string) => {
    getAuth(app).languageCode = languageCode
  },
  currentUser: () => getAuth(app).currentUser,
  signUpWithEmailAndPassword: (email: string, password: string, continueUrl?: string | null) =>
    createUserWithEmailAndPassword(getAuth(app), email, password)
      // send verification email
      .then((cred) => sendEmailVerification(cred.user, { url: buildAuthContinueUrl(continueUrl) }))
      .catch(handleError),
  signInWithEmailAndPassword: (email: string, password: string) =>
    signInWithEmailAndPassword(getAuth(app), email, password).catch(handleError),
  signInWithCustomToken: (token: string) => signInWithCustomToken(getAuth(app), token),
  signInWithGoogle: () => signInWithPopup(getAuth(app), googleAuthProvider).catch(handleError),
  signInWithLine: () => signInWithPopup(getAuth(app), lineAuthProvider).catch(handleError),
  sendPasswordResetEmail: (email: string) =>
    sendPasswordResetEmail(getAuth(app), email, {
      url: `${import.meta.env.VITE_BASE_URL}/login`,
    }),
  onAuthStateChanged: (nextOrObserver: NextOrObserver<User>) =>
    onAuthStateChanged(getAuth(app), nextOrObserver),
  signOut: () => signOut(getAuth(app)),
  updateEmail: (email: string, currentPassword: string) =>
    Promise.resolve(getAuth(app).currentUser)
      .then(async (currentUser) => {
        if (!currentUser) {
          throw new UnauthorizedError()
        }

        try {
          await verifyBeforeUpdateEmail(currentUser, email, {
            url: `${import.meta.env.VITE_BASE_URL}/login`,
          })
          return
        } catch (error) {
          if (!(error instanceof FirebaseError) || error.code !== "auth/requires-recent-login") {
            throw error
          }

          if (!currentUser.email) {
            throw new UnknownError()
          }

          const credential = EmailAuthProvider.credential(currentUser.email, currentPassword)
          await reauthenticateWithCredential(currentUser, credential)
          await verifyBeforeUpdateEmail(currentUser, email, {
            url: `${import.meta.env.VITE_BASE_URL}/login`,
          })
        }
      })
      .catch(handleError),
  updatePassword: (currentPassword: string, newPassword: string) =>
    Promise.resolve(getAuth(app).currentUser)
      .then((currentUser) => {
        if (!currentUser) {
          throw new UnauthorizedError()
        }

        if (!currentUser.email) {
          throw new UnknownError()
        }

        const credential = EmailAuthProvider.credential(currentUser.email, currentPassword)
        return reauthenticateWithCredential(currentUser, credential).then((credential) =>
          updatePassword(credential.user, newPassword)
        )
      })
      .catch(handleError),
} as const

const handleError = (error: unknown) => {
  if (!(error instanceof FirebaseError)) {
    throw error
  }

  if (error.code === "auth/invalid-credential") {
    throw new InvalidCredentialError()
  }

  if (error.code === "auth/email-already-in-use") {
    throw new EmailAlreadyInUseError()
  }

  if (error.code === "auth/account-exists-with-different-credential") {
    handleAccountExistsWithDifferentCredentialError(error)
  }

  throw error
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleAccountExistsWithDifferentCredentialError = (_e: FirebaseError) => {
  // TODO: handle nicely: https://stackoverflow.com/questions/46459591/firebase-login-by-same-email-different-provider/78203119#78203119
  // const email = e.customData?.email as string;
  // const credential = FacebookAuthProvider.credentialFromError(e);
  // if (!email || !credential) {
  //   throw e;
  // }
  throw new AccountExistsWithDifferentCredentialError()
}
